import React, { useRef, useState, useEffect, useCallback } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import IconButton from '@material-ui/core/IconButton';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import { Play as PlayIcon, Pause as PauseIcon } from 'lucide-react';

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
    maxWidth: 320,
    minHeight: 48,
    borderRadius: 12,
    background: theme.palette.background?.paper || '#ffffff',
    display: 'flex',
    alignItems: 'center',
    padding: 8,
    boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
    gap: 8,
  },
  controls: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  right: {
    display: 'flex',
    alignItems: 'center',
    flex: 1,
    gap: 6,
  },
  progressRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  time: {
    minWidth: 56,
    textAlign: 'right',
    color: theme.palette.text.secondary,
    fontSize: 12,
  },
  speedGroup: {
    marginLeft: 'auto',
  },
  progressBar: {
    flex: 1,
    height: 24,
    appearance: 'none',
  },
  speedButton: {
    minWidth: 40,
    padding: '4px 6px',
    fontSize: 12,
    marginLeft: 4,
  },
}));

function formatTime(time) {
  if (!isFinite(time) || time <= 0) return '00:00';
  const minutes = Math.floor(time / 60);
  const seconds = Math.floor(time % 60);
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

  const SPEED_ORDER = [1.0, 1.5, 2.0, 0.5];

const AudioPlayer = ({ src, controlsClassName, initialRate = 1.0, onPlay, onPause, onStop }) => {
  const classes = useStyles();
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [rate, setRate] = useState(SPEED_ORDER.indexOf(initialRate) !== -1 ? initialRate : 1.0);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const onLoaded = () => setDuration(audio.duration || 0);
    const onTime = () => setCurrentTime(audio.currentTime || 0);
    const onEnd = () => setPlaying(false);

    audio.addEventListener('loadedmetadata', onLoaded);
    audio.addEventListener('timeupdate', onTime);
    audio.addEventListener('ended', onEnd);

    return () => {
      audio.removeEventListener('loadedmetadata', onLoaded);
      audio.removeEventListener('timeupdate', onTime);
      audio.removeEventListener('ended', onEnd);
    };
  }, [src]);

  useEffect(() => {
    if (audioRef.current) audioRef.current.playbackRate = rate;
  }, [rate]);

  useEffect(() => {
    // reset when src changes
    setPlaying(false);
    setCurrentTime(0);
    setDuration(0);
  }, [src]);

  const togglePlay = useCallback(async () => {
    const audio = audioRef.current;
    if (!audio) return;
    try {
      if (playing) {
        audio.pause();
        setPlaying(false);
        onPause && onPause();
      } else {
        await audio.play();
        setPlaying(true);
        onPlay && onPlay();
      }
    } catch (err) {
      // play may fail due to browser policy
      console.warn('Audio play failed', err);
    }
  }, [playing, onPlay, onPause]);

  const stop = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.pause();
    audio.currentTime = 0;
    setPlaying(false);
    setCurrentTime(0);
    onStop && onStop();
  }, [onStop]);

  const handleSeek = (e) => {
    const audio = audioRef.current;
    if (!audio) return;
    const rect = e.target.getBoundingClientRect();
    const percent = (e.clientX - rect.left) / rect.width;
    const time = Math.max(0, Math.min(1, percent)) * duration;
    audio.currentTime = time;
    setCurrentTime(time);
  };

  const handleRangeChange = (e) => {
    const audio = audioRef.current;
    if (!audio) return;
    const time = Number(e.target.value);
    audio.currentTime = time;
    setCurrentTime(time);
  };

  return (
    <div className={classes.root + (controlsClassName ? ` ${controlsClassName}` : '')} role="group" aria-label="audio-player">
      <audio ref={audioRef} src={src} preload="metadata" />
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <IconButton aria-label={playing ? 'Pause' : 'Play'} onClick={togglePlay} size="small">
          {playing ? <PauseIcon size={18} /> : <PlayIcon size={18} />}
        </IconButton>
        <Button
          size="small"
          className={classes.speedButton}
          onClick={() => {
            const idx = SPEED_ORDER.indexOf(rate);
            const next = SPEED_ORDER[(idx + 1) % SPEED_ORDER.length] || SPEED_ORDER[0];
            setRate(next);
            try { localStorage.setItem('audioMessageRate', String(next)); } catch (e) {}
          }}
          aria-label={`Playback speed ${rate}x`}
        >
          {rate}x
        </Button>
        <div className={classes.right} style={{ minWidth: 0 }}>
          <div className={classes.progressRow} style={{ width: '100%' }}>
            <input
              aria-label="seek"
              type="range"
              min={0}
              max={duration || 0}
              step={0.01}
              value={currentTime}
              onChange={handleRangeChange}
              className={classes.progressBar}
            />
            <Typography className={classes.time}>{formatTime(duration)}</Typography>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AudioPlayer;
